import { useEffect, useState } from 'react';
import { Icon, StatusTag, icons, theme } from '@agentisai/design-system';
import type { TurnEvent } from '@agentisai/shared-types';

/**
 * Live turn narration (ADR-0042), Claude-Code style: one status line —
 * the latest checkpoint (or "Thinking…" before the first poll lands), the
 * elapsed time, the step count and the output tokens so far — with the full trail behind a native
 * disclosure. Mount it only while a turn is in flight: the clock starts at
 * mount. Shared by the workspace conversation and the create panel.
 *
 * Polite live region on the checkpoint label only — a handful per turn, not a
 * token stream, and not the ticking clock (that would announce every second).
 */
export function TurnActivity({ events }: { events: readonly TurnEvent[] }) {
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(startedAt);
  // Mirrors the native <details> open state, only to flip the chevron.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const seconds = Math.floor((now - startedAt) / 1000);
  const latest = events.length > 0 ? events[events.length - 1]!.label : 'Thinking…';
  const steps = events.length === 1 ? '1 step' : `${events.length} steps`;
  // Cumulative output tokens — off the newest event that carries a count (a
  // terminal event may not, and an older agent never sends one).
  const tokens = events.filter((e) => e.tokens !== undefined).at(-1)?.tokens;

  return (
    <div style={{ paddingInline: theme.space[4] }}>
      <details onToggle={(e) => setOpen(e.currentTarget.open)}>
        <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
          <StatusTag tone="info">
            {/* The affordance that says "this expands" — only once there's a trail. */}
            {events.length > 0 && (
              <Icon
                as={open ? icons.ChevronDown : icons.ChevronRight}
                size={12}
                aria-hidden="true"
                style={{ verticalAlign: 'middle', marginRight: theme.space[1] }}
              />
            )}
            {/* Live only once there is a real checkpoint: before that the typing
                indicator is already the status region, and two would compete. */}
            {events.length > 0 ? (
              <span role="status" aria-live="polite">
                {latest}
              </span>
            ) : (
              latest
            )}
            <span aria-hidden="true">
              {' '}
              · {seconds}s{events.length > 0 ? ` · ${steps}` : ''}
              {/* Hidden at 0: the routing/start checkpoints land before any reply. */}
              {tokens ? ` · ↓ ${formatTokens(tokens)} tokens` : ''}
            </span>
          </StatusTag>
        </summary>
        {events.length > 0 && (
          <ol
            aria-label="Turn activity"
            style={{
              margin: `${theme.space['1_5']} 0 0`,
              paddingLeft: theme.space[5],
              fontSize: theme.typography.fontSize.caption,
              color: theme.color.neutral.muted,
            }}
          >
            {events.map((e, i) => (
              <li key={`${e.at}-${i}`}>{e.label}</li>
            ))}
          </ol>
        )}
      </details>
    </div>
  );
}
TurnActivity.displayName = 'TurnActivity';

const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

/**
 * `2600` → `2.6k`, `950` → `950` — the Claude Code status-line style. Only the
 * `k` is lower-cased: `1.2m` would read as minutes beside the `12s` clock.
 */
function formatTokens(n: number): string {
  return compact.format(n).replace('K', 'k');
}
